import { selectCurrentScormFile } from "@/store/slices/courseSlice";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

interface ScromFileUnit {
  id?: string;
  name?: string;
}

interface ScromFileProps {
  unit?: ScromFileUnit | null;
}

type ScormData = Record<string, string>;

interface ScormApi {
  LMSInitialize: () => string;
  LMSFinish: () => string;
  LMSGetValue: (element: string) => string;
  LMSSetValue: (element: string, value: string) => string;
  LMSCommit: () => string;
  LMSGetLastError: () => string;
  LMSGetErrorString: (errorCode: string) => string;
  LMSGetDiagnostic: (errorCode: string) => string;
}

declare global {
  interface Window {
    API?: ScormApi;
  }
}

const calculateSessionTime = (startTime: Date, endTime: Date): string => {
  const diff = Math.floor((endTime.getTime() - startTime.getTime()) / 1000);
  const hours = Math.floor(diff / 3600)
    .toString()
    .padStart(2, "0");
  const minutes = Math.floor((diff % 3600) / 60)
    .toString()
    .padStart(2, "0");
  const seconds = Math.floor(diff % 60)
    .toString()
    .padStart(2, "0");
  return `${hours}:${minutes}:${seconds}`;
};

const ScromFile = ({ unit }: ScromFileProps) => {
  const scormFileData = useSelector(selectCurrentScormFile);
  const scormFile = scormFileData?.data;
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (scormFile?.scorm) {
      const startTime = new Date();

      const scormData: ScormData = {
        "cmi.core.student_id": "104",
        "cmi.core.student_name": "Student Name",
        "cmi.core.lesson_status": "incomplete",
        "cmi.core.score.raw": "0",
        "cmi.core.score.min": "0",
        "cmi.core.score.max": "100",
        "cmi.core.exit": "",
        "cmi.core.session_time": "00:00:00",
        "cmi.core.total_time": "00:00:00",
        "cmi.core.lesson_location": "0",
        "cmi.suspend_data": "",
      };

      window.API = {
        LMSInitialize: () => {
          console.log("LMSInitialize called");
          return "true";
        },
        LMSFinish: () => {
          console.log("LMSFinish called");
          scormData["cmi.core.session_time"] = calculateSessionTime(
            startTime,
            new Date()
          );
          return "true";
        },
        LMSGetValue: (element) => {
          console.log("LMSGetValue called for", element);
          if (element === "cmi.core.session_time") {
            scormData[element] = calculateSessionTime(startTime, new Date());
          }
          return scormData[element] || "";
        },
        LMSSetValue: (element, value) => {
          console.log("LMSSetValue called for", element, "with value", value);
          scormData[element] = value;

          if (element === "cmi.core.lesson_status" && value === "completed") {
            console.log("Lesson completed!");
          }

          return "true";
        },
        LMSCommit: () => {
          console.log("LMSCommit called");
          console.log("Current SCORM data:", scormData);
          return "true";
        },
        LMSGetLastError: () => "0",
        LMSGetErrorString: () => "No error",
        LMSGetDiagnostic: () => "No diagnostic information",
      };

      console.log("SCORM API initialized");

      return () => {
        if (window.API) {
          if (scormData["cmi.core.lesson_status"] !== "completed") {
            console.log("Cleaning up SCORM API on unmount");
          }
          delete window.API;
          console.log("SCORM API cleaned up");
        }
      };
    }
  }, [scormFile?.scorm]);

  if (!scormFile?.scorm) {
    return (
      <div className="rounded-[12px] flex items-center justify-center h-[calc(100vh-400px)] bg-white">
        <div className="text-center p-6">
          <h3 className="text-lg font-medium text-gray-700">
            No SCORM content available
          </h3>
          <p className="text-sm text-gray-500 mt-2">
            The selected unit does not have any associated SCORM content.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-[12px] overflow-auto h-[40rem] bg-white">
      <iframe
        src={scormFile.scorm}
        id="scormIframe"
        ref={iframeRef}
        title={unit?.name || "SCORM Content"}
        className="w-full h-full border-0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  );
};

export default ScromFile;
